// franklycatering #1429 — the pure rules behind the Events pipeline and the
// one-event page. No React and no queries, so every rule is unit-tested.
//
// Nothing here is stored. Version, Booking / Add-on / Option and the money
// figures are derived from the event's quotes each time they're shown.

import { canTransition, type EventRow, type EventStatus } from "@/hooks/useEvents";
import type { EventDetailsPatch } from "@/hooks/useEventsPipeline";

/** The values `events.stage` can hold (CHECK in the #1428 migration). */
export const EVENT_STAGES = [
  "lead",
  "quoting",
  "accepted",
  "booked",
  "final_details",
  "event_day",
  "post_event",
  "done",
  "lost",
  "cancelled",
] as const;
export type EventStage = (typeof EVENT_STAGES)[number];

/** Board columns shown by default, in order. */
export const OPEN_STAGES: EventStage[] = [
  "lead",
  "quoting",
  "accepted",
  "booked",
  "final_details",
  "event_day",
  "post_event",
];
/** Closed stages: picked in the Event status filter (#1557). */
export const CLOSED_STAGES: EventStage[] = ["done", "lost", "cancelled"];

export const EVENT_STAGE_LABELS: Record<EventStage, string> = {
  lead: "Lead",
  quoting: "Quoting",
  accepted: "Accepted",
  booked: "Booked",
  final_details: "Final details",
  event_day: "Event day",
  post_event: "Post-event",
  done: "Done",
  lost: "Lost",
  cancelled: "Cancelled",
};

/** A NULL stage (pre-backfill, or a racing insert) or an unknown one shows as Lead. */
export function normalizeStage(stage: string | null | undefined): EventStage {
  return (EVENT_STAGES as readonly string[]).includes(stage ?? "") ? (stage as EventStage) : "lead";
}

/** The quote columns these rules read. */
export interface StageQuote {
  id: string;
  quote_number: string;
  status: string;
  pipeline_stage: string;
  is_lost: boolean;
  total: number;
  amount_paid: number;
  payment_due_date: string | null;
  deposit_percent: number | null;
  created_at: string;
}

/** Rounds to cents. */
export const round2 = (n: number) => Math.round(n * 100) / 100;

/** Oldest first, `id` breaking ties, so the order is the same on every screen. */
export function sortQuotes<T extends Pick<StageQuote, "id" | "created_at">>(quotes: T[]): T[] {
  return [...quotes].sort((a, b) =>
    a.created_at === b.created_at ? a.id.localeCompare(b.id) : a.created_at < b.created_at ? -1 : 1,
  );
}

/** Version number per quote id: v1 is the event's first quote. */
export function quoteVersions(quotes: Pick<StageQuote, "id" | "created_at">[]): Map<string, number> {
  return new Map(sortQuotes(quotes).map((q, i) => [q.id, i + 1]));
}

/** Not declined and not lost: the rule derive_event_stage uses. */
export function isLiveQuote(q: Pick<StageQuote, "status" | "is_lost">): boolean {
  return q.status !== "declined" && !q.is_lost;
}

const BOOKING_PIPELINE_STAGES = ["deposit_pending", "booked", "won"];

/** Live and accepted, or dragged to a booking stage: the SQL's "booking quote" rule. */
export function isCommittedQuote(q: Pick<StageQuote, "status" | "is_lost" | "pipeline_stage">): boolean {
  return isLiveQuote(q) && (q.status === "accepted" || BOOKING_PIPELINE_STAGES.includes(q.pipeline_stage));
}

/** Committed quotes, oldest first. The first is the booking, the rest add-ons. */
export function committedQuotes<T extends StageQuote>(quotes: T[]): T[] {
  return sortQuotes(quotes.filter(isCommittedQuote));
}

export function bookingQuote<T extends StageQuote>(quotes: T[]): T | null {
  return committedQuotes(quotes)[0] ?? null;
}

export type QuoteRole = "booking" | "addon" | "option" | "closed";

export const QUOTE_ROLE_LABELS: Record<QuoteRole, string> = {
  booking: "Booking",
  addon: "Add-on",
  option: "Option",
  closed: "Closed",
};

export function labelQuotes(quotes: StageQuote[]): Map<string, QuoteRole> {
  const booking = bookingQuote(quotes);
  return new Map(
    quotes.map((q) => {
      const role: QuoteRole = !isLiveQuote(q)
        ? "closed"
        : q.id === booking?.id
          ? "booking"
          : isCommittedQuote(q)
            ? "addon"
            : "option";
      return [q.id, role];
    }),
  );
}

export interface EventMoney {
  /** True when at least one quote is committed; Quoted/Paid/Balance cover only those. */
  committed: boolean;
  quoted: number;
  paid: number;
  balance: number;
  /** The newest live quote's total, shown as "Latest quote" when nothing is committed. */
  latestTotal: number | null;
}

/** Money across the booking quote and its add-ons, so Paid + Balance = Quoted. */
export function eventMoney(quotes: StageQuote[]): EventMoney {
  const set = committedQuotes(quotes);
  const quoted = round2(set.reduce((s, q) => s + Number(q.total), 0));
  const paid = round2(set.reduce((s, q) => s + Number(q.amount_paid), 0));
  const live = sortQuotes(quotes.filter(isLiveQuote));
  return {
    committed: set.length > 0,
    quoted,
    paid,
    balance: round2(quoted - paid),
    latestTotal: live.length ? Number(live[live.length - 1].total) : null,
  };
}

export type NextPayment =
  | { kind: "none" }
  | { kind: "paid" }
  | { kind: "deposit" | "balance"; amount: number; dueDate: string | null };

export function nextPayment(quotes: StageQuote[]): NextPayment {
  const set = committedQuotes(quotes);
  if (set.length === 0) return { kind: "none" };
  const money = eventMoney(quotes);
  if (money.balance <= 0 && money.quoted > 0) return { kind: "paid" };
  const booking = set[0];
  const depositPercent = Number(booking.deposit_percent ?? 0);
  if (Number(booking.amount_paid) === 0 && depositPercent > 0) {
    return {
      kind: "deposit",
      amount: round2((Number(booking.total) * depositPercent) / 100),
      dueDate: booking.payment_due_date,
    };
  }
  const due = set
    .filter((q) => Number(q.total) - Number(q.amount_paid) > 0 && q.payment_due_date)
    .map((q) => q.payment_due_date as string)
    .sort()[0];
  return { kind: "balance", amount: money.balance, dueDate: due ?? null };
}

export function formatMoney(n: number): string {
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/** "Nov 20" from a yyyy-mm-dd date, read as a local date (no UTC shift). */
export function formatShortDate(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function describeNextPayment(p: NextPayment): string {
  if (p.kind === "none") return "—";
  if (p.kind === "paid") return "Paid in full";
  const label = p.kind === "deposit" ? "Deposit" : "Balance";
  return `${label} ${formatMoney(p.amount)}${p.dueDate ? ` due ${formatShortDate(p.dueDate)}` : ", no due date"}`;
}

function daysUntil(date: string, today: Date): number {
  const d = new Date(`${date}T00:00:00`);
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((d.getTime() - t.getTime()) / 86_400_000);
}

/** One line saying what the event needs next. */
export function nextStep(
  stage: EventStage,
  quotes: StageQuote[],
  eventDate: string | null,
  today: Date = new Date(),
): string {
  const live = sortQuotes(quotes.filter(isLiveQuote));
  const payment = nextPayment(quotes);
  switch (stage) {
    case "lead":
    case "quoting": {
      if (live.length === 0) return quotes.length === 0 ? "Send a quote" : "Send a new quote";
      const draft = [...live].reverse().find((q) => q.status === "draft");
      if (draft) return `Finish draft ${draft.quote_number}`;
      return `Waiting on the client for ${live[live.length - 1].quote_number}`;
    }
    case "accepted":
      return payment.kind === "deposit"
        ? `Collect the deposit: ${formatMoney(payment.amount)}`
        : `Collect payment: ${describeNextPayment(payment)}`;
    case "booked":
      return payment.kind === "paid" || payment.kind === "none"
        ? "Paid in full. Confirm details closer to the date"
        : describeNextPayment(payment);
    case "final_details": {
      if (!eventDate) return "Confirm final details";
      const n = daysUntil(eventDate, today);
      return `Confirm final details. Event in ${n} ${n === 1 ? "day" : "days"}`;
    }
    case "event_day":
      return "Event today";
    case "post_event": {
      const money = eventMoney(quotes);
      return money.committed && money.balance > 0
        ? `Wrap up: collect ${formatMoney(money.balance)}, send the thank-you and the review ask`
        : "Wrap up: send the thank-you and the review ask";
    }
    case "done": {
      const money = eventMoney(quotes);
      return money.committed && money.balance > 0
        ? `Event done. Balance ${formatMoney(money.balance)} outstanding`
        : "Event done";
    }
    case "lost":
    case "cancelled":
      return "Closed";
  }
}

export type DropResult = { ok: true; status: EventStatus } | { ok: false; reason: string };

const ACCEPTED_OR_LATER: EventStage[] = ["accepted", "booked", "final_details", "event_day", "post_event"];

/**
 * What a manual move to `column` (the card's Mark lost / Cancel event) does.
 * Stages are derived from quotes and payments, so a card can only be moved to
 * Lost or Cancelled, and only where the event's status allows it
 * (VALID_TRANSITIONS).
 */
export function dropTarget(
  fromStatus: EventStatus,
  fromStage: EventStage,
  column: EventStage,
  eventNumber: string,
): DropResult {
  if (column === fromStage) return { ok: false, reason: "" };
  if (column === "lost") {
    if (ACCEPTED_OR_LATER.includes(fromStage)) {
      return { ok: false, reason: `${eventNumber} has an accepted quote. Decline it on the quote first.` };
    }
    return canTransition(fromStatus, "lost")
      ? { ok: true, status: "lost" }
      : { ok: false, reason: `Can't mark ${eventNumber} lost while it's ${fromStatus.replace("_", " ")}.` };
  }
  if (column === "cancelled") {
    return canTransition(fromStatus, "cancelled")
      ? { ok: true, status: "cancelled" }
      : {
          ok: false,
          reason: `Can't cancel ${eventNumber} while it's ${fromStatus.replace("_", " ")}. Move or decline its quote first.`,
        };
  }
  return { ok: false, reason: "Stages move on their own from quotes and payments." };
}

/**
 * Makes a search term safe inside a PostgREST `or=(...)` filter: commas and
 * parentheses split that grammar, and % * \ " ' are pattern or quoting
 * characters. An empty result means "no search".
 */
export function sanitizeSearchTerm(raw: string): string {
  return raw
    .replace(/[,()%*\\"']/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
}

/** Tailwind classes for a chip's status dot. */
export function quoteStatusDot(q: Pick<StageQuote, "status" | "is_lost">): string {
  if (q.is_lost || q.status === "declined") return "bg-destructive";
  if (q.status === "accepted") return "bg-success";
  if (q.status === "sent") return "bg-primary";
  return "bg-muted-foreground/50";
}

export function quoteStatusLabel(q: Pick<StageQuote, "status" | "is_lost">): string {
  if (q.is_lost) return "Lost";
  return q.status.charAt(0).toUpperCase() + q.status.slice(1);
}

export type EventsView = "pipeline" | "list";

/**
 * Which /admin/events view the URL asks for (#1429). An explicit ?view= wins.
 * Otherwise ?incomplete=1, the Quotes page's "unfinished leads" link, means
 * the List, and anything else `fallback` (the person's saved view, #1557).
 */
export function eventsViewFrom(params: URLSearchParams, fallback: EventsView = "pipeline"): EventsView {
  const view = params.get("view");
  if (view === "list" || view === "pipeline") return view;
  return params.get("incomplete") === "1" ? "list" : fallback;
}

/** A draft nobody has filled in: status draft, total 0, no line items (#1429 discard, #1557 Delete draft). */
export function isEmptyDraft(q: { status: string; total: number | string; line_items?: unknown[] | null }): boolean {
  return q.status === "draft" && Number(q.total) === 0 && (q.line_items ?? []).length === 0;
}

/**
 * False when deleting the draft would fire trg_cleanup_manual_quote_event,
 * which removes or cancels a `manual` event once its last quote goes.
 */
export function draftDiscardable(eventSource: string | null, otherQuotes: number): boolean {
  return !(eventSource === "manual" && otherQuotes === 0);
}

// Event details form (#1429).
export type EventDetailField = keyof Required<EventDetailsPatch>;
export type EventDetailValues = Record<EventDetailField, string>;

export const EVENT_DETAIL_FIELDS: { key: EventDetailField; label: string; type: string }[] = [
  { key: "client_name", label: "Client name", type: "text" },
  { key: "client_email", label: "Email", type: "email" },
  { key: "client_phone", label: "Phone", type: "tel" },
  { key: "event_date", label: "Date", type: "date" },
  { key: "event_time", label: "Time", type: "text" },
  { key: "guest_count", label: "Guests", type: "number" },
  { key: "delivery_address", label: "Address", type: "text" },
];

// The quotes' own CHECK (quotes_event_date_range_check). The #1428 sync copies
// the event's date to its quotes and fails without a word outside this range.
export const EVENT_DETAILS_MIN_DATE = "2020-01-01";
export const EVENT_DETAILS_MAX_DATE = "2031-12-31";

export function valuesFrom(event: Pick<EventRow, EventDetailField>): EventDetailValues {
  return Object.fromEntries(
    EVENT_DETAIL_FIELDS.map(({ key }) => [key, event[key] == null ? "" : String(event[key])]),
  ) as EventDetailValues;
}

/**
 * The changed fields as a patch, or errors. A field that has a value can't be
 * saved blank: the #1428 sync never copies a blank to the quotes, so the two
 * would disagree.
 */
export function validateEventDetails(
  initial: EventDetailValues,
  next: EventDetailValues,
): { errors: Partial<Record<EventDetailField, string>>; patch: EventDetailsPatch } {
  const errors: Partial<Record<EventDetailField, string>> = {};
  const patch: Record<string, string | number> = {};
  for (const { key } of EVENT_DETAIL_FIELDS) {
    const before = initial[key].trim();
    const after = next[key].trim();
    if (after === before) continue;
    if (!after) {
      errors[key] = "Can't be cleared here: it would no longer match the quotes";
      continue;
    }
    if (key === "event_date" && (after < EVENT_DETAILS_MIN_DATE || after > EVENT_DETAILS_MAX_DATE)) {
      errors[key] = "Pick a date from 2020 to 2031";
      continue;
    }
    if (key === "guest_count") {
      const n = Number(after);
      if (!Number.isInteger(n) || n < 1) {
        errors[key] = "Guests must be a whole number, 1 or more";
        continue;
      }
      patch[key] = n;
      continue;
    }
    patch[key] = after;
  }
  return { errors, patch: patch as EventDetailsPatch };
}
